// @migration-in-progress
import { devLog } from '../utils/devLog';
import React, { useState, useEffect, useMemo } from 'react';
import { X, AlertTriangle, Phone, CheckSquare, Users, Edit2, FileUp } from 'lucide-react';
import { EMERGENCY_CHECKLISTS, getChecklistIcon } from '../config/emergencyChecklists';
import EmergencyChecklistModal from './EmergencyChecklistModal';
import EmergencyChecklistEditModal from './EmergencyChecklistEditModal';
import ChecklistImportWizard from './tailwind/modules/operations/import/ChecklistImportWizard';
import { useStorageState } from '../hooks/useStorageState';
import { useVessel } from '../contexts/VesselContext';

/**
 * EmergencyPanel - Slide-out emergency procedures interface
 * 
 * Features:
 * - Right-side slide-out panel
 * - Tabbed interface for Checklists and Contacts
 * - Vessel-specific emergency procedures
 * - Persistent tab state and emergency logging
 * - Large touch targets for emergency use
 */
const EmergencyPanel = ({ isOpen, onClose }) => {
  // Get currentVessel from context - stable after Fix #1
  const { currentVessel } = useVessel();
  
  const [activeTab, setActiveTab] = useState('checklists');
  const [selectedChecklist, setSelectedChecklist] = useState(null);
  const [showChecklistModal, setShowChecklistModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingChecklist, setEditingChecklist] = useState(null);
  const [startedChecklists, setStartedChecklists] = useState(new Set());
  const [customizedChecklists, setCustomizedChecklists] = useState({});
  const [showImportWizard, setShowImportWizard] = useState(false);
  
  // vessel-scoped
  const { value: emergencyCustomizations, setValue: setEmergencyCustomizations } = useStorageState('emergency_customizations', null);
  
  // Track which checklists have saved progress
  const [checklistsWithProgress, setChecklistsWithProgress] = useState(new Set());

  // Helper to normalize vessel type for comparison
  const normalizeVesselType = (type) => {
    return (type || '').toLowerCase().trim();
  };

  // Filter checklists based on vessel configuration
  const filteredChecklists = useMemo(() => {
    if (!currentVessel) {
      // Return only basic critical emergencies if no vessel config
      return EMERGENCY_CHECKLISTS.checklists.filter(checklist => 
        checklist.type === 'fire' || 
        checklist.type === 'mob' || 
        checklist.type === 'abandon'
      );
    }

    // Get all checklists
    const allChecklists = EMERGENCY_CHECKLISTS.checklists;
    
    // Filter based on vessel applicability
    return allChecklists.filter(checklist => {
      // Check if checklist has applicableFor function
      if (checklist.applicableFor && typeof checklist.applicableFor === 'function') {
        return checklist.applicableFor(currentVessel);
      }
      
      // For older format, check vesselTypes
      if (checklist.vesselTypes) {
        if (checklist.vesselTypes.includes('all')) return true;
        const vesselType = normalizeVesselType(currentVessel.type || currentVessel.type);
        return checklist.vesselTypes.some(type => 
          normalizeVesselType(type) === vesselType
        );
      }
      
      // Default to including if no specific filter
      return true;
    });
  }, [currentVessel]);

  // Sort checklists by priority (critical > high > medium)
  const sortedChecklists = useMemo(() => {
    const priorityOrder = { critical: 0, high: 1, medium: 2 };
    return [...filteredChecklists].sort((a, b) => {
      const aPriority = priorityOrder[a.priority] || 999;
      const bPriority = priorityOrder[b.priority] || 999;
      if (aPriority !== bPriority) return aPriority - bPriority;
      return (a.sortOrder || 999) - (b.sortOrder || 999);
    });
  }, [filteredChecklists]);

  // Prevent body scroll when panel is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  // Load customizations and check for saved progress on mount
  useEffect(() => {
    const loadCustomizations = () => {
      try {
        if (emergencyCustomizations) {
          setCustomizedChecklists(emergencyCustomizations);
        }
      } catch (error) {

      }
    };
    
    const checkSavedProgress = () => {
      // Note: emergency progress tracking needs to be migrated to vessel-scoped storage
      // For now, we'll reset this on each mount
      setChecklistsWithProgress(new Set());
      setStartedChecklists(new Set());
    };
    
    loadCustomizations();
    checkSavedProgress();
  }, [emergencyCustomizations]);

  // Handle checklist selection
  const handleChecklistClick = (checklist) => {
    // Apply any customizations
    const customized = customizedChecklists[checklist.id];
    const checklistToUse = customized ? { ...checklist, ...customized } : checklist;
    
    setSelectedChecklist(checklistToUse);
    setShowChecklistModal(true);
    setStartedChecklists(prev => new Set([...prev, checklist.id]));
    
    // Log checklist access - will be handled by audit system
    // Removed direct localStorage access
  };

  // Handle edit checklist
  const handleEditChecklist = (checklist, event) => {
    event.stopPropagation(); // Prevent opening the checklist modal
    
    // Apply any existing customizations
    const customized = customizedChecklists[checklist.id];
    const checklistToEdit = customized ? { ...checklist, ...customized } : checklist;
    
    setEditingChecklist(checklistToEdit);
    setShowEditModal(true);
  };

  // Handle save edited checklist
  const handleSaveChecklist = (updatedChecklist) => {
    try {
      // Save customizations
      const newCustomizations = {
        ...customizedChecklists,
        [updatedChecklist.id]: {
          steps: updatedChecklist.steps,
          lastModified: new Date().toISOString(),
          isCustomized: true
        }
      };
      
      setCustomizedChecklists(newCustomizations);
      // vessel-scoped
      setEmergencyCustomizations(newCustomizations);
      
      devLog('Emergency checklist customized successfully');
    } catch (error) {

    }
  };

  // Handle import completion from AI wizard
  const handleImportComplete = (importedChecklist) => {
    // Save imported emergency checklist to customizations
    if (importedChecklist && importedChecklist.items) {
      const newChecklistId = `imported_${Date.now()}`;
      const newCustomizations = {
        ...customizedChecklists,
        [newChecklistId]: {
          id: newChecklistId,
          title: importedChecklist.title || 'Imported Checklist',
          steps: importedChecklist.items.map((item, idx) => ({
            id: `step_${idx}`,
            text: item.text,
            required: item.required !== false
          })),
          isImported: true,
          importedAt: new Date().toISOString(),
          lastModified: new Date().toISOString()
        }
      };
      setCustomizedChecklists(newCustomizations);
      setEmergencyCustomizations(newCustomizations);
    }
    setShowImportWizard(false);
  };

  // Get emergency contacts based on vessel configuration
  const getEmergencyContacts = () => {
    const contacts = [
      // Always include basic emergency services
      { id: 'coastguard', category: 'SAR', name: 'HM Coastguard', number: '999 or VHF Ch 16', priority: 'critical' },
      { id: 'rnli', category: 'SAR', name: 'RNLI', number: 'VHF Ch 16', priority: 'critical' },
    ];

    // Add port-specific contacts if available
    if (currentVessel?.homePort) {
      contacts.push({
        id: 'port',
        category: 'PORT',
        name: `${currentVessel.homePort} Port Control`,
        number: 'Check port guide',
        priority: 'high'
      });
    }

    // Add medical contacts if vessel carries medical stores
    if (currentVessel?.medicalCategory && currentVessel.medicalCategory !== 'none') {
      contacts.push({
        id: 'medical',
        category: 'MEDICAL',
        name: 'Radio Medical Advice',
        number: 'Via Coastguard',
        priority: 'high'
      });
    }

    // Add company contacts
    contacts.push(
      { id: 'company', category: 'COMPANY', name: 'Company Emergency Line', number: 'Add your number', priority: 'high' },
      { id: 'insurance', category: 'SUPPORT', name: 'P&I Club Emergency', number: 'Add your number', priority: 'medium' }
    );

    return contacts;
  };

  const emergencyContacts = getEmergencyContacts();

  // Group contacts by category
  const contactsByCategory = useMemo(() => {
    const grouped = {
      SAR: [],
      PORT: [],
      COMPANY: [],
      MEDICAL: [],
      SUPPORT: []
    };
    
    emergencyContacts.forEach(contact => {
      const category = contact.category || 'COMPANY';
      if (grouped[category]) {
        grouped[category].push(contact);
      }
    });
    
    return grouped;
  }, [emergencyContacts]);

  if (!isOpen) return null;

  return (
    <>
      {/* Overlay */}
      <div 
        className="emergency-panel-overlay" 
        onClick={showChecklistModal ? undefined : onClose}
        style={showChecklistModal ? { pointerEvents: 'none' } : {}}
      />
      
      {/* Panel */}
      <div className="emergency-panel">
        {/* Header */}
        <div className="emergency-panel-header">
          <h2>Emergency Response Procedures</h2>
          <button 
            className="emergency-panel-close"
            onClick={onClose}
            aria-label="Close Emergency Panel"
          >
            <X size={24} />
          </button>
        </div>

        {/* Vessel Info Bar */}
        {currentVessel && (
          <div className="emergency-vessel-info">
            <span className="vessel-name">{currentVessel.name || 'Unnamed Vessel'}</span>
            <span className="vessel-type">
              {currentVessel.type || currentVessel.type || 'Unknown'} - 
              Category {currentVessel.category || '?'}
            </span>
          </div>
        )}

        {/* Tab Bar */}
        <div className="emergency-panel-tabs">
          <button
            className={`emergency-tab ${activeTab === 'checklists' ? 'active' : ''}`}
            onClick={() => setActiveTab('checklists')}
          >
            <CheckSquare size={18} />
            Checklists
          </button>
          <button
            className={`emergency-tab ${activeTab === 'contacts' ? 'active' : ''}`}
            onClick={() => setActiveTab('contacts')}
          >
            <Phone size={18} />
            Contacts
          </button>
        </div>

        {/* Content */}
        <div className="emergency-panel-content">
          {activeTab === 'checklists' && (
            <div className="emergency-checklists">
              {/* Priority indicator */}
              <div className="priority-legend">
                <span className="priority-item critical">
                  <span className="priority-dot"></span>Critical
                </span>
                <span className="priority-item high">
                  <span className="priority-dot"></span>High Priority
                </span>
                <span className="priority-item medium">
                  <span className="priority-dot"></span>Medium Priority
                </span>
              </div>

              {/* Import button */}
              <div className="mb-4 flex justify-end">
                <button
                  onClick={() => setShowImportWizard(true)}
                  className="flex items-center gap-2 px-4 py-2 border-2 border-dashed border-red-300
                    text-red-600 rounded-lg hover:bg-red-50 transition-colors text-sm"
                >
                  <FileUp size={16} />
                  Import Emergency Checklist
                  <span className="text-xs bg-red-100 px-1.5 py-0.5 rounded">PRO</span>
                </button>
              </div>

              {sortedChecklists.length === 0 ? (
                <div className="emergency-fallback">
                  <AlertTriangle size={48} className="fallback-icon" />
                  <p>No emergency checklists configured for this vessel. Please check vessel settings.</p>
                </div>
              ) : (
                <div className="emergency-checklist-grid">
                  {sortedChecklists.map(checklist => {
                    const Icon = getChecklistIcon(checklist.type);
                    const priorityClass = checklist.priority || 'medium';
                    
                    const isCustomized = customizedChecklists[checklist.id];
                    
                    return (
                      <div
                        key={checklist.id}
                        className={`emergency-checklist-card priority-${priorityClass} ${
                          startedChecklists.has(checklist.id) ? 'started' : ''
                        } ${isCustomized ? 'customized' : ''}`}
                        onClick={() => handleChecklistClick(checklist)}
                      >
                        <button
                          className="checklist-edit-btn"
                          onClick={(e) => handleEditChecklist(checklist, e)}
                          title="Edit checklist"
                        >
                          <Edit2 size={16} />
                        </button>
                        <div className="checklist-card-header">
                          <Icon size={24} />
                          <h3>{checklist.title}</h3>
                        </div>
                        {checklist.duration && (
                          <div className="checklist-duration">
                            Est. {checklist.duration}
                          </div>
                        )}
                        <div className="checklist-badges">
                          {checklistsWithProgress.has(checklist.id) && (
                            <span className="status-badge started">In Progress</span>
                          )}
                          {isCustomized && (
                            <span className="status-badge customized">Customized</span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {activeTab === 'contacts' && (
            <div className="emergency-contacts">
              {emergencyContacts.length === 0 ? (
                <div className="emergency-fallback">
                  <Phone size={48} className="fallback-icon" />
                  <p>Emergency contacts not configured. Please add contact information.</p>
                </div>
              ) : (
                <div className="emergency-contacts-list">
                  {/* Search & Rescue */}
                  {contactsByCategory.SAR.length > 0 && (
                    <div className="contacts-group">
                      <h3>Search & Rescue (SAR)</h3>
                      <div className="contacts-priority-notice">
                        <AlertTriangle size={16} />
                        <span>MAYDAY: VHF Ch 16 or 999</span>
                      </div>
                      {contactsByCategory.SAR.map(contact => (
                        <div key={contact.id} className="contact-card">
                          <div className="contact-name">{contact.name}</div>
                          <div className="contact-number">{contact.number}</div>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* Port Authorities */}
                  {contactsByCategory.PORT.length > 0 && (
                    <div className="contacts-group">
                      <h3>Port Authorities</h3>
                      {contactsByCategory.PORT.map(contact => (
                        <div key={contact.id} className="contact-card">
                          <div className="contact-name">{contact.name}</div>
                          <div className="contact-number">{contact.number}</div>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* Company Contacts */}
                  {contactsByCategory.COMPANY.length > 0 && (
                    <div className="contacts-group">
                      <h3>Company Contacts</h3>
                      {contactsByCategory.COMPANY.map(contact => (
                        <div key={contact.id} className="contact-card">
                          <div className="contact-name">{contact.name}</div>
                          <div className="contact-number">{contact.number}</div>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* Medical & Support */}
                  {(contactsByCategory.MEDICAL.length > 0 || contactsByCategory.SUPPORT.length > 0) && (
                    <div className="contacts-group">
                      <h3>Medical & Support Services</h3>
                      {[...contactsByCategory.MEDICAL, ...contactsByCategory.SUPPORT].map(contact => (
                        <div key={contact.id} className="contact-card">
                          <div className="contact-name">{contact.name}</div>
                          <div className="contact-number">{contact.number}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Emergency Checklist Modal */}
      {showChecklistModal && selectedChecklist && (
        <EmergencyChecklistModal
          isOpen={showChecklistModal}
          onClose={() => {
            setShowChecklistModal(false);
            setSelectedChecklist(null);
            // Refresh progress status - handled by vessel-scoped storage now
            // Progress tracking will be managed through audit system
          }}
          checklist={selectedChecklist}
        />
      )}
      
      {/* Emergency Checklist Edit Modal */}
      {showEditModal && editingChecklist && (
        <EmergencyChecklistEditModal
          isOpen={showEditModal}
          onClose={() => {
            setShowEditModal(false);
            setEditingChecklist(null);
          }}
          checklist={editingChecklist}
          onSave={handleSaveChecklist}
        />
      )}

      {/* Checklist Import Wizard */}
      <ChecklistImportWizard
        isOpen={showImportWizard}
        onClose={() => setShowImportWizard(false)}
        onComplete={handleImportComplete}
        initialType="emergency"
      />
    </>
  );
};

export default EmergencyPanel;